import { useState } from 'react';

import { useSearchPageState } from '@klareospace/pharos-lib-front-utils';
import type { UseSearchPageStateReturn } from '@klareospace/pharos-lib-front-utils';
import type { Carrier, CarrierType, SortDirection } from '@klareospace/pharos-lib-types';

import type { SearchCarriersParams } from '../api';
import type { SiteCarrierSortableColumn } from '../types/carrier.types';
import { findValidity, computeSiteCarrierStatus, toValidCarrierTypes } from '../utils';
import type { SiteCarrierStatus } from '../utils';
import { useCarriers } from './use-carriers';

// ─── Types ────────────────────────────────────────────────────────────────────

export type { SiteCarrierStatus };

interface Validity {
  from: Date | null | undefined;
  to: Date | null | undefined;
}

export interface SiteCarrier extends Carrier {
  validity: Validity | undefined;
  status: SiteCarrierStatus;
}

export interface UseSiteCarriersPageOptions {
  siteId: string;
  spaceId: string;
}

export interface UseSiteCarriersPageReturn {
  carriers: SiteCarrier[];
  currentPage: number;
  error: Error | null;
  isLoading: boolean;
  onPageChange: (newPage: number) => void;
  onSearchChange: (value: string) => void;
  onSort: (sortBy: SiteCarrierSortableColumn, sortDirection: SortDirection | null) => void;
  onTypeFilterChange: (values: string[]) => void;
  pageSize: number;
  searchValue: string;
  sortBy: SiteCarrierSortableColumn | undefined;
  sortDirection: SortDirection | undefined;
  totalItems: number;
  totalPages: number;
  typeFilter: CarrierType[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toSiteCarriersParams(
  searchState: UseSearchPageStateReturn<SiteCarrierSortableColumn>,
  siteId: string,
  typeFilter: CarrierType[],
): SearchCarriersParams {
  return {
    enabledOnSiteId: siteId,
    page: searchState.currentPage,
    pageSize: searchState.pageSize,
    search: searchState.debouncedSearch,
    sort: searchState.sort,
    types: typeFilter.length > 0 ? typeFilter : undefined,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useSiteCarriersPage({ spaceId, siteId }: UseSiteCarriersPageOptions): UseSiteCarriersPageReturn {
  const searchState = useSearchPageState<SiteCarrierSortableColumn>();
  const [typeFilter, setTypeFilter] = useState<CarrierType[]>([]);

  const { data, error, isLoading } = useCarriers(toSiteCarriersParams(searchState, siteId, typeFilter));

  const carriers: SiteCarrier[] = (data?.items ?? []).map((carrier) => {
    const validity = findValidity(carrier, spaceId, siteId);
    return {
      ...carrier,
      validity,
      status: computeSiteCarrierStatus(validity),
    };
  });

  const onTypeFilterChange = (values: string[]): void => {
    setTypeFilter(toValidCarrierTypes(values));
    searchState.resetPage();
  };

  return {
    carriers,
    currentPage: searchState.currentPage,
    error: error ?? null,
    isLoading,
    onPageChange: searchState.onPageChange,
    onSearchChange: searchState.onSearchChange,
    onSort: searchState.onSort,
    onTypeFilterChange,
    pageSize: searchState.pageSize,
    searchValue: searchState.searchValue,
    sortBy: searchState.sortBy,
    sortDirection: searchState.sortDirection,
    totalItems: data?.total ?? 0,
    totalPages: data?.totalPages ?? 1,
    typeFilter,
  };
}
